/**
 * Account Detail Page
 *
 * Tabbed detail view for account management.
 * Includes 8 tabs: Overview, Users, Billing, Support, Phone Numbers, Discounts, Audit Log, Notes
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  Input,
} from '@respectly/ui';
import { cn } from '@respectly/ui';
import { useImpersonationStore } from '../../stores/useImpersonationStore';
import {
  ArrowLeft,
  LayoutDashboard,
  Users,
  CreditCard,
  HeadphonesIcon,
  Phone,
  ScrollText,
  StickyNote,
  Ticket,
  PauseCircle,
  PlayCircle,
  UserCog,
  AlertTriangle,
  Mail,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useAccountsStore } from '../../stores/useAccountsStore';
import { AccountStatusBadge, BillingStatusBadge } from '../../components/accounts';
import { TenantDisputesPanel } from '../../components/accounts/TenantDisputesPanel';

// Tab Components
import { OverviewTab } from '../../components/accounts/tabs/OverviewTab';
import { UsersTab } from '../../components/accounts/tabs/UsersTab';
import { BillingTab } from '../../components/accounts/tabs/BillingTab';
import { SupportTab } from '../../components/accounts/tabs/SupportTab';
import { PhoneNumbersTab } from '../../components/accounts/tabs/PhoneNumbersTab';
import { AuditLogTab } from '../../components/accounts/tabs/AuditLogTab';
import { NotesTab } from '../../components/accounts/tabs/NotesTab';
import { DiscountsTab } from '../../components/accounts/tabs/DiscountsTab';

// ============================================
// Tab Configuration
// ============================================

const tabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'users', label: 'Team Members', icon: Users },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'support', label: 'Support', icon: HeadphonesIcon },
  { id: 'phone-numbers', label: 'Phone Numbers', icon: Phone },
  { id: 'discounts', label: 'Discounts', icon: Ticket },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
  { id: 'notes', label: 'Notes', icon: StickyNote },
] as const;

type TabId = (typeof tabs)[number]['id'];

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

// ============================================
// Component
// ============================================

export function AccountDetail() {
  const { id, tab } = useParams({ strict: false }) as { id: string; tab?: string };
  const navigate = useNavigate();

  const {
    activeAccount,
    isLoadingAccount,
    accountError,
    isPerformingAction,
    loadAccountDetail,
    suspendAccount,
    reactivateAccount,
    extendTrial,
  } = useAccountsStore();

  const { startImpersonation, isStarting } = useImpersonationStore();

  // Current tab state
  const [activeTab, setActiveTab] = useState<TabId>((tab as TabId) || 'overview');

  // Dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [extendTrialDialogOpen, setExtendTrialDialogOpen] = useState(false);
  const [extendTrialDays, setExtendTrialDays] = useState<number>(7);
  const [extendTrialReason, setExtendTrialReason] = useState('');
  const [extendTrialResult, setExtendTrialResult] = useState<
    | { kind: 'success'; helcimSynced: boolean; newTrialEndsAt: string }
    | { kind: 'partial'; helcimSyncError: string; newTrialEndsAt: string }
    | { kind: 'error'; message: string }
    | null
  >(null);

  // Load account on mount
  useEffect(() => {
    if (id) {
      loadAccountDetail(id);
    }
  }, [id, loadAccountDetail]);

  // Sync tab with URL
  useEffect(() => {
    if (tab && tabs.some((t) => t.id === tab)) {
      setActiveTab(tab as TabId);
    }
  }, [tab]);

  // Handle tab change
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab as TabId);
    navigate({
      to: '/accounts/$id/$tab',
      params: { id, tab: newTab },
      replace: true,
    });
  };

  // Handle back navigation
  const handleBack = () => {
    navigate({ to: '/accounts' });
  };

  // Quick action handlers
  const handleSuspend = () => setSuspendDialogOpen(true);

  const handleSuspendConfirm = async () => {
    if (!activeAccount || !suspendReason.trim()) return;
    await suspendAccount(activeAccount.id, { reason: suspendReason.trim(), notifyUsers: true });
    setSuspendDialogOpen(false);
    setSuspendReason('');
  };

  const handleReactivate = async () => {
    if (!activeAccount) return;
    await reactivateAccount(activeAccount.id, { notifyUsers: true });
  };

  const handleImpersonate = async () => {
    if (!activeAccount) return;
    await startImpersonation(activeAccount.id, {});
  };

  const handleExtendTrial = () => {
    setExtendTrialDays(7);
    setExtendTrialReason('');
    setExtendTrialResult(null);
    setExtendTrialDialogOpen(true);
  };

  const handleExtendTrialConfirm = async () => {
    if (!activeAccount) return;
    const result = await extendTrial(
      activeAccount.id,
      extendTrialDays,
      extendTrialReason.trim() || undefined,
    );
    if (!result.success) {
      setExtendTrialResult({ kind: 'error', message: 'Failed to extend trial. Please try again.' });
      return;
    }
    if (result.helcimSyncError) {
      setExtendTrialResult({
        kind: 'partial',
        helcimSyncError: result.helcimSyncError,
        newTrialEndsAt: result.newTrialEndsAt!,
      });
      return;
    }
    setExtendTrialResult({
      kind: 'success',
      helcimSynced: result.helcimSynced ?? false,
      newTrialEndsAt: result.newTrialEndsAt!,
    });
  };

  // Determine available actions
  const canSuspend =
    activeAccount?.status === 'active' || activeAccount?.status === 'trial';
  const canReactivate = activeAccount?.status === 'suspended';
  const canExtendTrial = activeAccount?.trialStatus === 'trialing';

  // Compute the previewed new trial end date for the extend modal
  const previewNewTrialEnd = (() => {
    if (!activeAccount?.trialEndsAt || !extendTrialDays) return null;
    const base = new Date(activeAccount.trialEndsAt);
    const now = new Date();
    const startFrom = base > now ? base : now;
    return new Date(startFrom.getTime() + extendTrialDays * 86_400_000);
  })();

  // ============================================
  // Render
  // ============================================

  if (isLoadingAccount) {
    return <LoadingSkeleton onBack={handleBack} />;
  }

  if (accountError || !activeAccount) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
          Account not found
        </h2>
        <p className="mt-2 text-slate-500">
          The account you're looking for doesn't exist or you don't have access.
        </p>
        <Button className="mt-6" onClick={handleBack}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Accounts
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="mb-4 -ml-2 text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Accounts
        </Button>

        {/* Account Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {/* Left: Account Info */}
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 text-xl font-bold text-slate-600 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300">
              {activeAccount.name.charAt(0).toUpperCase()}
            </div>

            {/* Name & Details */}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  {activeAccount.name}
                </h1>
                <AccountStatusBadge status={activeAccount.status} />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <span className="font-mono">/{activeAccount.slug}</span>
                {activeAccount.industry && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">•</span>
                    <span>{activeAccount.industry}</span>
                  </>
                )}
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span>{activeAccount.plan.name}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <BillingStatusBadge status={activeAccount.billingStatus} size="sm" />
                <span className="text-xs text-slate-400">
                  {activeAccount.userCount} users • {activeAccount.contactCount} contacts
                </span>
              </div>
            </div>
          </div>

          {/* Right: Quick Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {canExtendTrial && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExtendTrial}
                disabled={isPerformingAction}
                className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
              >
                <Clock className="mr-1.5 h-4 w-4" />
                Extend Trial
              </Button>
            )}
            {canSuspend && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSuspend}
                disabled={isPerformingAction}
                className="text-amber-600 border-amber-200 hover:bg-amber-50 hover:border-amber-300"
              >
                <PauseCircle className="mr-1.5 h-4 w-4" />
                Suspend
              </Button>
            )}
            {canReactivate && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReactivate}
                disabled={isPerformingAction}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
              >
                <PlayCircle className="mr-1.5 h-4 w-4" />
                Reactivate
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleImpersonate}
              disabled={isStarting || isPerformingAction}
            >
              <UserCog className="mr-1.5 h-4 w-4" />
              {isStarting ? 'Starting...' : 'Impersonate'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Disputes panel — auto-renders nothing when this tenant has no disputes */}
      <motion.div variants={itemVariants}>
        <TenantDisputesPanel tenantId={activeAccount.id} />
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        {/* Tab List - Horizontally Scrollable on Mobile */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="inline-flex h-auto gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800/50 min-w-max">
            {tabs.map((tabItem) => {
              const Icon = tabItem.icon;
              const isActive = activeTab === tabItem.id;
              return (
                <button
                  key={tabItem.id}
                  onClick={() => handleTabChange(tabItem.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-900 dark:text-white'
                      : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tabItem.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'overview' && <OverviewTab account={activeAccount} />}
          {activeTab === 'users' && <UsersTab accountId={activeAccount.id} />}
          {activeTab === 'billing' && <BillingTab account={activeAccount} />}
          {activeTab === 'support' && <SupportTab accountId={activeAccount.id} />}
          {activeTab === 'phone-numbers' && <PhoneNumbersTab accountId={activeAccount.id} />}
          {activeTab === 'discounts' && <DiscountsTab accountId={activeAccount.id} />}
          {activeTab === 'audit' && <AuditLogTab accountId={activeAccount.id} />}
          {activeTab === 'notes' && <NotesTab accountId={activeAccount.id} />}
        </div>
      </motion.div>

      {/* ── Suspend Dialog ──────────────────────────── */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Suspend Account
            </DialogTitle>
          </DialogHeader>

          {/* Warning callout */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-900/20">
            <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-300">
              <li className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                All users will be notified by email
              </li>
              <li className="flex items-center gap-2">
                <span className="h-3.5 w-3.5 shrink-0 text-center font-bold leading-none">×</span>
                Users will immediately lose access
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="suspend-reason">Reason for suspension</Label>
            <Input
              id="suspend-reason"
              placeholder="e.g. Payment overdue, Policy violation..."
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSuspendDialogOpen(false); setSuspendReason(''); }}>
              Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspendConfirm}
              disabled={!suspendReason.trim() || isPerformingAction}
            >
              <PauseCircle className="mr-1.5 h-4 w-4" />
              {isPerformingAction ? 'Suspending...' : 'Suspend Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Extend Trial Dialog ─────────────────────── */}
      <Dialog
        open={extendTrialDialogOpen}
        onOpenChange={(open) => {
          setExtendTrialDialogOpen(open);
          if (!open) {
            setExtendTrialResult(null);
            setExtendTrialReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              Extend Trial
            </DialogTitle>
          </DialogHeader>

          {extendTrialResult?.kind === 'success' ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800/50 dark:bg-emerald-900/20">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900 dark:text-emerald-200">
                  <p className="font-medium">Trial extended successfully</p>
                  <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                    New trial ends {new Date(extendTrialResult.newTrialEndsAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    {extendTrialResult.helcimSynced ? ' • Helcim synced' : ' • No Helcim sync needed'}
                  </p>
                </div>
              </div>
            </div>
          ) : extendTrialResult?.kind === 'partial' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-900/20">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 dark:text-amber-200">
                  <p className="font-medium">Extended locally — Helcim sync failed</p>
                  <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
                    Helcim may still attempt to charge the card on its original schedule. Review the Helcim subscription manually.
                  </p>
                  <p className="mt-1 text-xs font-mono text-amber-700/80 dark:text-amber-300/80 break-words">
                    {extendTrialResult.helcimSyncError}
                  </p>
                </div>
              </div>
            </div>
          ) : extendTrialResult?.kind === 'error' ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800/50 dark:bg-red-900/20">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-900 dark:text-red-200">{extendTrialResult.message}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="extend-trial-days">Days to add</Label>
                <Input
                  id="extend-trial-days"
                  type="number"
                  min={1}
                  max={90}
                  value={extendTrialDays}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setExtendTrialDays(Math.max(1, Math.min(90, v)));
                  }}
                  autoFocus
                />
                <p className="text-xs text-slate-500">Between 1 and 90 days. Counted from the current trial end (or today if the trial has already lapsed).</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="extend-trial-reason">Reason (optional)</Label>
                <Input
                  id="extend-trial-reason"
                  placeholder="e.g. Sales requested 2 more weeks for evaluation"
                  value={extendTrialReason}
                  onChange={(e) => setExtendTrialReason(e.target.value)}
                />
              </div>

              {previewNewTrialEnd && activeAccount?.trialEndsAt && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/30">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Currently ends</span>
                    <span className="font-medium text-slate-900 dark:text-slate-200">
                      {new Date(activeAccount.trialEndsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-3">
                    <span className="text-slate-500">Will end</span>
                    <span className="font-semibold text-blue-700 dark:text-blue-300">
                      {previewNewTrialEnd.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          <DialogFooter>
            {extendTrialResult ? (
              <Button
                onClick={() => {
                  setExtendTrialDialogOpen(false);
                  setExtendTrialResult(null);
                  setExtendTrialReason('');
                }}
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setExtendTrialDialogOpen(false); setExtendTrialReason(''); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleExtendTrialConfirm}
                  disabled={!extendTrialDays || extendTrialDays < 1 || isPerformingAction}
                >
                  <Clock className="mr-1.5 h-4 w-4" />
                  {isPerformingAction ? 'Extending...' : `Extend by ${extendTrialDays} day${extendTrialDays === 1 ? '' : 's'}`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// ============================================
// Loading Skeleton
// ============================================

function LoadingSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="mb-4 -ml-2 text-slate-500"
      >
        <ArrowLeft className="mr-1.5 h-4 w-4" />
        Back to Accounts
      </Button>

      <div className="flex items-start gap-4">
        <Skeleton className="h-14 w-14 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-5 w-32" />
        </div>
      </div>

      <Skeleton className="h-10 w-full max-w-2xl" />

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-6 w-1/4" />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
